import { CartLinesUpdateEvent } from '@shopify/events';

// Cart drawer "You might also like" row. Tapping anything in a suggestion (photo, name, color, size, Add)
// opens a pop-up window with a large photo, description, color and size choices, and Add to Cart.
// Uses document-level listeners because the drawer markup is re-rendered after every cart change.

// Adds one variant to the cart and refreshes the drawer. Returns an error message, or null on success.
async function addVariant(variantId, anchor) {
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  anchor.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sectionIds.join(','),
      }),
    });
    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message || 'Could not add to cart');

    const cartResponse = await fetch(Theme.routes.cart_url + '.json', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    const cart = await cartResponse.json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'bmp-cart-suggest',
        itemCount: 1,
        sections: data.sections,
        didError: false,
      },
    });
    return null;
  } catch (error) {
    deferred.reject(error);
    return error && error.message ? error.message : 'Could not add to cart.';
  }
}

// Any tap inside a suggestion opens the pop-up
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const item = target.closest('.cart-suggest__item');
  if (!item) return;
  const link = item.querySelector('.cart-suggest__name');
  if (!link) return;
  event.preventDefault();
  openModal(link.getAttribute('href') || '');
});

// ---------- Pop-up window ----------

let modal = null;
let modalState = null;

const MODAL_CSS = `
dialog.bmp-sm { border: 0; padding: 0; width: min(560px, 94vw); max-height: 92vh; border-radius: 12px; overflow: hidden; background: #ffffff; color: #2a2320; }
dialog.bmp-sm::backdrop { background: rgba(0, 0, 0, 0.55); }
.bmp-sm__panel { position: relative; max-height: 92vh; overflow-y: auto; }
.bmp-sm__close { position: absolute; top: 8px; right: 8px; z-index: 2; width: 44px; height: 44px; border: 0; border-radius: 50%; background: rgba(255,255,255,0.92); color: #2a2320; font-size: 28px; line-height: 1; cursor: pointer; }
.bmp-sm__img { display: block; width: 100%; max-height: 46vh; object-fit: contain; background: #f5f2ec; }
.bmp-sm__body { padding: 16px 18px 20px; display: flex; flex-direction: column; gap: 12px; }
.bmp-sm__title { margin: 0; font-size: 1.25rem; font-weight: 700; line-height: 1.25; }
.bmp-sm__price { font-size: 1.125rem; font-weight: 600; }
.bmp-sm__label { display: block; margin: 0 0 6px; font-size: 0.9375rem; font-weight: 700; }
.bmp-sm__select { width: 100%; height: 48px; padding: 0 10px; font-size: 16px; color: #2a2320; background: #ffffff; border: 2px solid #2a2320; border-radius: 6px; }
.bmp-sm__chips { display: flex; flex-wrap: wrap; gap: 8px; }
.bmp-sm__chip { min-width: 58px; height: 48px; padding: 0 12px; font-size: 1rem; font-weight: 700; color: #2a2320; background: #ffffff; border: 2px solid #2a2320; border-radius: 6px; cursor: pointer; }
.bmp-sm__chip[aria-pressed='true'] { color: #ffffff; background: #2a2320; }
.bmp-sm__chip:disabled { opacity: 0.35; text-decoration: line-through; cursor: default; }
.bmp-sm__desc { max-height: 150px; overflow-y: auto; font-size: 0.9375rem; line-height: 1.5; }
.bmp-sm__desc p { margin: 0 0 8px; }
.bmp-sm__msg { margin: 0; min-height: 0; font-size: 0.9375rem; font-weight: 600; color: #970118; }
.bmp-sm__msg:empty { display: none; }
.bmp-sm__add { width: 100%; height: 54px; font-size: 1rem; font-weight: 700; text-transform: uppercase; color: #ffffff; background: #970118; border: 0; border-radius: 6px; cursor: pointer; }
.bmp-sm__add:disabled { opacity: 0.6; cursor: default; }
.bmp-sm__more { align-self: center; font-size: 0.9375rem; color: #2a2320; text-decoration: underline; }
`;

function ensureModal() {
  if (modal && modal.isConnected) return modal;
  if (!document.getElementById('bmp-sm-style')) {
    const style = document.createElement('style');
    style.id = 'bmp-sm-style';
    style.textContent = MODAL_CSS;
    document.head.appendChild(style);
  }
  modal = document.createElement('dialog');
  modal.className = 'bmp-sm';
  modal.setAttribute('aria-label', 'Shirt details');
  modal.addEventListener('click', (e) => {
    if (e.target === modal) modal.close();
  });
  // Inside the drawer's own dialog so the drawer's focus handling treats it as part of the drawer.
  const host = document.querySelector('#cart-drawer dialog') || document.body;
  host.appendChild(modal);
  return modal;
}

function imgUrl(src, width) {
  if (!src) return '';
  return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
}

function money(cents) {
  return '$' + (Number(cents) / 100).toFixed(2);
}

function normalizeOptions(p) {
  return p.options.map((o, i) => {
    if (typeof o === 'string') {
      const values = [];
      p.variants.forEach((v) => {
        const val = v['option' + (i + 1)];
        if (val != null && values.indexOf(val) === -1) values.push(val);
      });
      return { name: o, values };
    }
    return { name: o.name, values: o.values };
  });
}

function matchVariants(state, requireAll) {
  return state.p.variants.filter((v) =>
    state.opts.every((o, i) => {
      const picked = state.sel[o.name];
      if (!picked) return !requireAll;
      return v['option' + (i + 1)] === picked;
    })
  );
}

function updateModal() {
  const s = modalState;
  if (!s) return;
  const root = modal;

  // Photo: first matching variant that has its own image
  const withImage = matchVariants(s, false).find((v) => v.featured_image && v.featured_image.src);
  const src = withImage ? withImage.featured_image.src : s.p.featured_image;
  const img = root.querySelector('.bmp-sm__img');
  if (img && src) img.src = imgUrl(src, 800);

  // Price
  const full = matchVariants(s, true)[0];
  const priceEl = root.querySelector('.bmp-sm__price');
  if (priceEl) priceEl.textContent = money(full ? full.price : s.p.price);

  // Size chips: pressed state, and dim sizes that are sold out in the chosen color
  s.opts.forEach((o, i) => {
    if (o.name.toLowerCase() !== 'size') return;
    root.querySelectorAll('.bmp-sm__chip').forEach((chip) => {
      const value = chip.dataset.value;
      chip.setAttribute('aria-pressed', s.sel[o.name] === value ? 'true' : 'false');
      const others = s.p.variants.filter(
        (v) =>
          v['option' + (i + 1)] === value &&
          s.opts.every((oo, j) => j === i || !s.sel[oo.name] || v['option' + (j + 1)] === s.sel[oo.name])
      );
      chip.disabled = !others.some((v) => v.available);
    });
  });
}

function openModal(href) {
  if (!href) return;
  const jsUrl = href.split('?')[0] + '.js';

  fetch(jsUrl, { headers: { Accept: 'application/json' } })
    .then((r) => {
      if (!r.ok) throw new Error('bad response');
      return r.json();
    })
    .then((p) => {
      const opts = normalizeOptions(p);
      const sel = {};
      opts.forEach((o) => {
        if (o.name.toLowerCase() !== 'size') sel[o.name] = o.values[0];
      });
      modalState = { p, opts, sel, href };
      drawModal();
    })
    .catch(() => {
      window.location.href = href;
    });
}

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function drawModal() {
  const s = modalState;
  const root = ensureModal();

  const optionsHtml = s.opts
    .map((o) => {
      if (o.name.toLowerCase() === 'size') {
        const chips = o.values
          .map(
            (v) =>
              '<button type="button" class="bmp-sm__chip" data-value="' + escapeHtml(v) + '" aria-pressed="false">' + escapeHtml(v) + '</button>'
          )
          .join('');
        return '<div><span class="bmp-sm__label">' + escapeHtml(o.name) + '</span><div class="bmp-sm__chips" data-opt="' + escapeHtml(o.name) + '">' + chips + '</div></div>';
      }
      const options = o.values
        .map((v) => '<option value="' + escapeHtml(v) + '"' + (s.sel[o.name] === v ? ' selected' : '') + '>' + escapeHtml(v) + '</option>')
        .join('');
      return '<div><label class="bmp-sm__label">' + escapeHtml(o.name) + '<select class="bmp-sm__select" data-opt="' + escapeHtml(o.name) + '" style="margin-top:6px">' + options + '</select></label></div>';
    })
    .join('');

  root.innerHTML =
    '<div class="bmp-sm__panel">' +
    '<button type="button" class="bmp-sm__close" aria-label="Close">&times;</button>' +
    '<img class="bmp-sm__img" alt="' + escapeHtml(s.p.title) + '" src="' + imgUrl(s.p.featured_image, 800) + '">' +
    '<div class="bmp-sm__body">' +
    '<h2 class="bmp-sm__title">' + escapeHtml(s.p.title) + '</h2>' +
    '<div class="bmp-sm__price"></div>' +
    optionsHtml +
    '<div class="bmp-sm__desc">' + (s.p.description || '') + '</div>' +
    '<p class="bmp-sm__msg" role="status"></p>' +
    '<button type="button" class="bmp-sm__add">Add to cart</button>' +
    '<a class="bmp-sm__more" href="' + escapeHtml(s.href) + '">See the full page</a>' +
    '</div></div>';

  root.querySelector('.bmp-sm__close').addEventListener('click', () => root.close());

  root.querySelectorAll('.bmp-sm__select').forEach((el) => {
    el.addEventListener('change', () => {
      s.sel[el.dataset.opt] = el.value;
      root.querySelector('.bmp-sm__msg').textContent = '';
      updateModal();
    });
  });

  root.querySelectorAll('.bmp-sm__chip').forEach((chip) => {
    chip.addEventListener('click', () => {
      const name = chip.parentElement.dataset.opt;
      s.sel[name] = chip.dataset.value;
      root.querySelector('.bmp-sm__msg').textContent = '';
      updateModal();
    });
  });

  const addBtn = root.querySelector('.bmp-sm__add');
  const msg = root.querySelector('.bmp-sm__msg');
  addBtn.addEventListener('click', async () => {
    const missing = s.opts.find((o) => !s.sel[o.name]);
    if (missing) {
      msg.textContent = 'Please pick a ' + missing.name.toLowerCase();
      return;
    }
    const variant = matchVariants(s, true)[0];
    if (!variant || !variant.available) {
      msg.textContent = 'Sorry, that combination is sold out.';
      return;
    }
    addBtn.disabled = true;
    addBtn.textContent = 'Adding...';
    const error = await addVariant(variant.id, addBtn);
    if (error) {
      msg.textContent = error;
      addBtn.disabled = false;
      addBtn.textContent = 'Add to cart';
      return;
    }
    root.close();
  });

  updateModal();
  if (!root.open) root.showModal();
}
